import { UploadFileInfo, UploadFileStatus } from '@progress/kendo-react-upload';
import { produce } from 'immer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getErrorMessage,
  setValidationErrors,
  showErrorToast,
} from '../../../../../../../common/global/helpers';
import Button from '../../../../../../../common/widgets/Button';
import Dialog from '../../../../../../../common/widgets/Dialog';
import FileInput from '../../../../../../../common/widgets/FileInput';
import Icons from '../../../../../../../common/widgets/Icons';
import {
  Notification,
  NotificationType,
} from '../../../../../../../common/widgets/Notification';
import TextArea from '../../../../../../../common/widgets/TextArea';
import { ErrorState, ErrorType } from '../../../../../../benefit/models/Errors';
import { SoCodeTypes } from '../../../../../enums';
import {
  CodeModel,
  ImportedPinCodesModel,
  ManuallyAddedCodesModel,
} from '../../../../../models/codes';
import { resources } from '../../../../../resources/resources';
import { codesService } from '../../../../../services/codes';

interface ManuallyAddedCodesDialogProps {
  setCodes: (codes: { valid: CodeModel[]; invalid: CodeModel[] }) => void;
  soCodeType?: SoCodeTypes;
  closeDialog: () => void;
}

export const ManuallyAddedCodesDialog = (
  props: ManuallyAddedCodesDialogProps,
) => {
  const { setCodes, closeDialog, soCodeType } = props;
  const [codeType, setCodeType] = useState<SoCodeTypes>(
    soCodeType ?? SoCodeTypes.Default,
  );
  const [files, setFiles] = useState<UploadFileInfo[]>([]);
  const [isContinueDisabled, setIsContinueDisabled] = useState<boolean>(true);
  const [showManualDialog, setShowManualDialog] = useState<boolean>(false);
  const [errors, setErrors] = useState<ErrorState>({});
  const { t: translate } = useTranslation();

  const onSaveUrl = async (files: UploadFileInfo[]) => {
    const allowedExtensions = ['.xlsx'];
    const maxFileSize = 4194304; // 4MB
    const file = files[0];
    const fileExtension = file.extension ?? '';
    const fileSize = file.size ?? 0;
    if (!allowedExtensions.includes(fileExtension) || fileSize > maxFileSize) {
      setErrors(
        produce(errors, d => {
          d.file = [
            {
              code: resources.codes.manual.invalidFile,
              placeholders: {},
              severity: 'error',
              target: 'file',
            },
          ];
        }),
      );
      return { uid: file.uid };
    }

    setErrors(
      produce(errors, d => {
        d.file = [];
      }),
    );

    setFiles(files);
    const rawFile = file.getRawFile!();

    const formData = new FormData();
    formData.append('file', rawFile);

    const resp =
      codeType === SoCodeTypes.Default
        ? await codesService.importDefaultCodes(formData)
        : await codesService.importPinCodes(formData);

    if (resp.hasErrors) {
      if (resp.errorType === ErrorType.ValidationError) {
        setValidationErrors(resp, setErrors);
        const newFiles = files.map(e => ({
          ...e,
          status: UploadFileStatus.Uploaded,
        }));
        setFiles(newFiles);
      } else {
        showErrorToast(translate(resources.errors.generic));
      }

      return { uid: file.uid };
    }

    if (codeType === SoCodeTypes.Default) {
      const data: ManuallyAddedCodesModel =
        resp.data as ManuallyAddedCodesModel;

      const newFiles = files.map(e => ({
        ...e,
        status: UploadFileStatus.Uploaded,
      }));
      setFiles(newFiles);
      setCodes({
        valid: data.validCodes.map(e => ({
          code: e,
          type: SoCodeTypes.Default,
        })),
        invalid: data.invalidCodes.map(e => ({
          code: e.code,
          type: SoCodeTypes.Default,
          invalidReason: e.reason,
        })),
      });
      setIsContinueDisabled(false);
    } else {
      const data: ImportedPinCodesModel = resp.data as ImportedPinCodesModel;

      const newFiles = files.map(e => ({
        ...e,
        status: UploadFileStatus.Uploaded,
      }));
      setFiles(newFiles);
      setCodes({
        valid: data.validCodes.map(e => ({
          code: e.code,
          pin: e.pin,
          type: SoCodeTypes.Pin,
        })),
        invalid: data.invalidCodes.map(e => ({
          code: e.code,
          pin: e.pin,
          type: SoCodeTypes.Pin,
          invalidReason: e.reason,
        })),
      });
      setIsContinueDisabled(false);
    }

    return { uid: file.uid };
  };

  if (showManualDialog)
    return (
      <ManualDialog
        onBackClick={() => setShowManualDialog(false)}
        onPinClick={() => {
          setCodeType(SoCodeTypes.Pin);
          setShowManualDialog(false);
        }}
        closeDialog={closeDialog}
        setCodes={setCodes}
        soCodeType={soCodeType}
      />
    );

  return (
    <Dialog
      closeDialog={closeDialog}
      showCloseIcon
      className="dialog-merchants-edit"
    >
      <div className="dialog-content">
        <div className="dialog-title">
          {translate(resources.codes.manual.dialog.title)}
        </div>
        {!soCodeType && (
          <div className="code-type-selector">
            <span
              style={{
                backgroundColor:
                  codeType === SoCodeTypes.Default ? '#ff220d' : undefined,
                color: codeType === SoCodeTypes.Default ? '#ffffff' : undefined,
              }}
              onClick={() => setCodeType(SoCodeTypes.Default)}
            >
              {translate(resources.codes.defaultCodes)}
            </span>
            <span
              style={{
                backgroundColor:
                  codeType === SoCodeTypes.Pin ? '#ff220d' : undefined,
                color: codeType === SoCodeTypes.Pin ? '#ffffff' : undefined,
              }}
              onClick={() => setCodeType(SoCodeTypes.Pin)}
            >
              {translate(resources.codes.pinCodes)}
            </span>
          </div>
        )}
        {codeType === SoCodeTypes.Default && (
          <div className="dialog-text centered mb-20">
            {translate(resources.codes.manual.dialog.defaultCodes.methods)}
          </div>
        )}
        {codeType === SoCodeTypes.Pin && (
          <div className="dialog-text centered mb-20">
            {translate(resources.codes.manual.dialog.pinCodes.methods)}
          </div>
        )}
        <div>
          {codeType === SoCodeTypes.Default && (
            <div className="code-dialog-box">
              <Button
                className="btn btn-outline codes-dialog-btn"
                onClick={() => setShowManualDialog(true)}
                icon={<Icons.MCodesAdd />}
              >
                <span className="btn-text">
                  {translate(resources.codes.manual.dialog.addManually.lable)}
                </span>
              </Button>
              <span>
                {translate(resources.codes.manual.dialog.addManually.info)}
              </span>
            </div>
          )}

          <div className="code-file-upload">
            <FileInput
              withCredentials={false}
              multiple={false}
              errors={errors.file}
              files={files}
              saveUrl={async () => onSaveUrl(files)}
              removeUrl={async () => {
                setFiles([]);
                setCodes({ valid: [], invalid: [] });
                return { uid: '' };
              }}
            />
            {errors.null && (
              <Notification
                type={NotificationType.Error}
                scrollUp={false}
                messages={errors.null.map(e => ({
                  message: getErrorMessage(e, translate),
                }))}
              />
            )}
            <a
              onClick={() =>
                codeType === SoCodeTypes.Default
                  ? codesService.downloadDefaultCodesTemplate(
                      translate(resources.codes.templates.default),
                    )
                  : codesService.downloadPinCodesTemplate(
                      translate(resources.codes.templates.pin),
                    )
              }
              className="btn-link"
            >
              <span className="btn-icon">
                <Icons.MDownloadTemplate />
              </span>
              <span className="btn-text">
                {translate(resources.codes.manual.dialog.downloadTemplate)}
              </span>
            </a>
          </div>
        </div>
      </div>
      <div className="dialog-actions vertical">
        <Button
          onClick={closeDialog}
          className="btn btn-solid"
          disabled={isContinueDisabled}
        >
          {translate(resources.continue)}
        </Button>
      </div>
    </Dialog>
  );
};

interface ManualDialogProps {
  closeDialog: () => void;
  onBackClick: () => void;
  onPinClick: () => void;
  setCodes: (codes: { valid: CodeModel[]; invalid: CodeModel[] }) => void;
  soCodeType?: SoCodeTypes;
}

const ManualDialog = (props: ManualDialogProps) => {
  const { closeDialog, onBackClick, onPinClick, setCodes, soCodeType } = props;
  const [internalCodes, setInternalCodes] = useState<string>('');
  const [isContinueButtonLoading, setIsContinueButtonLoading] =
    useState<boolean>(false);
  const { t: translate } = useTranslation();

  const onContinueClick = async () => {
    setIsContinueButtonLoading(true);
    const resp = await codesService.validateDefaultCodes(
      internalCodes.split('\n').filter(e => !!e),
    );
    setIsContinueButtonLoading(false);

    if (resp.hasErrors) {
      showErrorToast(translate(resources.errors.generic));
    } else {
      setCodes({
        valid: resp.data.validCodes.map(e => ({
          code: e,
          type: SoCodeTypes.Default,
        })),
        invalid: resp.data.invalidCodes.map(e => ({
          code: e.code,
          type: SoCodeTypes.Default,
          invalidReason: e.reason,
        })),
      });
      closeDialog();
    }
  };

  return (
    <Dialog
      closeDialog={closeDialog}
      showCloseIcon={!isContinueButtonLoading}
      className="dialog-merchants-edit"
    >
      <div className="dialog-content">
        <div className="dialog-title">
          {translate(resources.codes.manual.dialog.title)}
        </div>
        {!soCodeType && (
          <div className="code-type-selector">
            <span
              style={{
                backgroundColor: '#ff220d',
                color: '#ffffff',
              }}
            >
              {translate(resources.codes.defaultCodes)}
            </span>
            <span onClick={!isContinueButtonLoading ? onPinClick : undefined}>
              {translate(resources.codes.pinCodes)}
            </span>
          </div>
        )}

        <div
          className="dialog-text centered mb-20"
          dangerouslySetInnerHTML={{
            __html: translate(resources.codes.manual.dialog.manual.info),
          }}
        />
        <div className="m-form-margin-0">
          <TextArea
            label={translate(resources.codes.manual.dialog.codes.label)}
            placeholder={translate(
              resources.codes.manual.dialog.codes.placeholder,
            )}
            value={internalCodes}
            onChange={e => setInternalCodes(e.value)}
            mandatory
            rows={12}
          />
        </div>
      </div>
      <div className="dialog-actions horizontal">
        <Button
          onClick={onBackClick}
          className="btn btn-outline"
          disabled={isContinueButtonLoading}
        >
          {translate(resources.back)}
        </Button>
        <Button
          onClick={onContinueClick}
          className="btn btn-solid"
          disabled={!internalCodes}
          loading={isContinueButtonLoading}
        >
          {translate(resources.continue)}
        </Button>
      </div>
    </Dialog>
  );
};
